import {
    IACResults,
    IOrderItems,
    IOrderObject,
    IOrderTotals,
    IPGResponse,
    IRPCData,
    IUpdatePayload
} from '~api/CartAPI/ICartApiSdk';
import { IResponse } from '@estee/elc-http-transport';
import ICart, { ICartTotals } from '~domain/entities/Cart/ICart';
import { mapKits } from '~api/KitsAPI/utils/kitsMapper';
import ICartItem from '~domain/entities/CartItem/ICartItem';
import { toBoolean } from '~utils/ToBoolean';

import { diContainer } from '~setup/diContainer';
import { serviceNames } from '~setup/CartService';
import { getSkuMedia } from '~utils/getSkuMedia';
import { getDiscountType } from '~api/utils/skuDiscounts';
import { reduceToCartOrderData } from '~api/CartRestAPI/utils/utils';

export const defaultValue = {
    totals: {
        SUB_TOTAL: 0,
        PRODUCT_SUM_W_TAX: 0,
        SHIPPING_W_TAX: 0,
        TOTAL_COST_W_TAX: 0,
        TOTAL_TAX: 0,
        display_sales_tax_cart: false
    },
    order: { items: [], samples: [] },
    appliedOffersData: {}
};

export const formatBagUpdate = (
    { skus, quantity, token, cartId, collectionId }: IUpdatePayload,
    quantityOverride?: number
) => {
    const basePayload = {
        _TOKEN: token,
        _SUBMIT: 'cart',
        CART_ID: cartId,
        SKU_BASE_ID: skus,
        QTY: quantityOverride ?? quantity
    };
    const payload =
        cartId && collectionId
            ? { ...basePayload, CART_ID: cartId, COLLECTION_ID: collectionId }
            : basePayload;

    return [payload];
};

const defaultCartItem = {
    result: { CARTITEM: { ITEM_QUANTITY: 0 } as IOrderItems, PREVIOUS_ITEM_QUANTITY: 0 }
};

const normalizeTransData = (transData?: IOrderObject | null) =>
    ({ response: { data: [{ result: { value: transData } }] } } as IRPCData<IOrderObject>);

const mapQtyAdded = ({
    result: { CARTITEM, PREVIOUS_ITEM_QUANTITY }
}: IACResults | undefined = defaultCartItem) => {
    const newCartItem = {
        ...CARTITEM,
        ITEM_QUANTITY: CARTITEM?.ITEM_QUANTITY - PREVIOUS_ITEM_QUANTITY
    };

    return newCartItem;
};

const getMappedPrices = ({ item, totalTax }: { item: IOrderItems; totalTax: number }) => {
    const discountAmount = item?.DISCOUNT_AMOUNT ?? 0;
    const hasMarkdownDiscount = !!item?.['sku.PRICE2'] && !discountAmount;
    const basePrice = hasMarkdownDiscount ? item?.['sku.PRICE2'] : item?.['sku.PRICE'];
    const discountedUnitPrice = item?.discountedAppliedPrice
        ? item.discountedAppliedPrice / item?.ITEM_QUANTITY
        : item?.UNIT_PRICE;

    return {
        appliedPrice: item?.APPLIED_PRICE,
        discountType: getDiscountType(item),
        unitPriceWithTax: basePrice + totalTax,
        appliedPriceWithTax: item?.APPLIED_PRICE + discountAmount,
        discountPercent: item?.DISCOUNT_PCT || item?.discountPercent || 0,
        hasMarkdownDiscount,
        basePrice,
        discountedUnitPrice,
        discountedUnitPriceWithTax: discountedUnitPrice + totalTax,
        itemLoyaltyPoints: item?.loyaltyRewardCostInPoints || 0
    };
};

const mapCartItems = ({
    orderItems,
    totals,
    cartAppliedOffersData
}: {
    orderItems: IOrderItems[];
    totals: IOrderTotals | null;
    cartAppliedOffersData: IOrderObject['appliedOffersData'];
}) => {
    const { config } = diContainer.get(serviceNames.config);
    const enableReplenishment = config?.cartConfig?.enableReplenishment;

    return orderItems.reduce((itemsAccum, item) => {
        const { thumb, altThumb } = getSkuMedia(item);
        const isItemFromAutomaticOffer =
            item?.OFFER_CODE &&
            cartAppliedOffersData?.[item.OFFER_CODE]?.activation === 'automatic';

        const cartItem: ICartItem = {
            collectionId: item?.COLLECTION_ID,
            cartId: item?.CART_ID,
            ...getMappedPrices({ item, totalTax: totals?.TOTAL_TAX || 0 }),
            skuId: item['sku.SKU_BASE_ID'],
            quantity: item?.ITEM_QUANTITY,
            offerCode: item?.OFFER_CODE,
            isOutOfStock: toBoolean(item['sku.isOutOfStock']),
            isReplenishable: toBoolean(item['sku.REFILLABLE']),
            enableReplenishment: enableReplenishment,
            replenishmentOptions: item?.replenishmentItemForm?.REPLENISHMENT_FREQ?.optionsGroup,
            replenishmentInfo: {
                collectionId: item?.COLLECTION_ID,
                replenishmentToken: item?.replenishmentItemForm?.csrf_token
            },
            discountableSku: item?.discountable,
            product: {
                productId: item['prod.PRODUCT_ID'],
                name: item['prod.PROD_RGN_NAME'],
                description: item['prod.PROD_RGN_SUBHEADING'],
                skuLabel: item['sku.label'],
                url: item['prod.url'],
                price: item['sku.PRICE'],
                shade: item['sku.SHADENAME'],
                shadeCode: item['sku.HEX_VALUE_STRING'] || item['sku.HEX_VALUE'],
                size: item['sku.PRODUCT_SIZE'],
                thumb,
                altThumb,
                isSample: item.isSample || false,
                isPricedSample: !item.isSampleItem && item.isSample,
                baseName: item['prod.PROD_BASE_NAME'],
                maxAllowedQty: item?.maxAllowedQty,
                isHazmat: item['prod.isHazmat']
            },
            isGiftItem: item.isGiftItem || false,
            isDonationItem: item.isDonationItem || false,
            isFreeVirtualSkuItem: item.isFreeVirtualSkuItem || false,
            isLoyaltyReward: item.isLoyaltyReward || false,
            isWpOffer: item.isWpOffer || false,
            isEngravable: item.IS_ENGRAVABLE || item['sku.IS_ENGRAVABLE'],
            discountType: getDiscountType(item),
            isAutomaticOffer: !!isItemFromAutomaticOffer
        };

        return [...itemsAccum, cartItem];
    }, []);
};

export const parseTotals = (response: IResponse<IPGResponse<IOrderObject>[]>): ICartTotals => {
    const data = response?.data?.[0].result.value as IOrderObject;

    return {
        totalCost: data?.trans?.TOTAL_COST || data?.trans?.totals?.TOTAL_COST_W_TAX,
        formattedTotalCost: data?.trans?.['formatted.TOTAL_COST'] || data?.formattedTotal,
        subTotal: data?.subtotal_no_tax || 0,
        formattedSubtotal: data?.formattedSubtotalNoTax || '',
        taxRate: data?.formattedTaxRate,
        isVatApplied: toBoolean(data?.trans?.total_applied_vat),
        totalTax:
            data?.trans?.total_applied_vat ??
            (data?.trans?.TOTAL_APPLIED_TAX || data?.TOTAL_APPLIED_TAX),
        formattedTotalTax: data?.formattedTotalTax || data?.trans?.['formatted.TOTAL_APPLIED_TAX'],
        shippingTax: data?.trans?.TOTAL_SHIPPING_APPLIED_PRICE,
        formattedShippingTax: data?.trans?.['formatted.TOTAL_SHIPPING_APPLIED_PRICE'],
        discount: {
            isDiscounted: data?.trans?.isDiscounted,
            totalPrice: data?.trans?.TOTAL_ITEMS_BASE_PRICE,
            formattedTotalPrice: data?.trans?.['formatted.TOTAL_ITEMS_BASE_PRICE'],
            discountAmount: data?.trans?.totalItemsDiscount,
            formattedDiscountAmount: data?.trans?.formattedTotalItemsDiscount,
            subtotalWithoutDonation: data?.trans?.getDonationsDerivedSubtotal,
            offersSummary: data.offers_summary
        },
        maxTotalItemLimit: data.max_total_item_limit,
        hasSalesTax: data.display_sales_tax_cart,
        giftwrapAppliedPrice: data?.trans?.TOTAL_GIFTWRAP_APPLIED_PRICE,
        formattedGiftwrapAppliedPrice: data?.trans?.['formatted.TOTAL_GIFTWRAP_APPLIED_PRICE'],
        donationPrice: data?.trans?.getDonation,
        paymentInstallments: data?.trans?.paymentInstallments || {},
        potentialLoyaltyPoints: Number(data?.trans?.loyaltyPoints) || 0,
        retailDeliveryFee: data?.trans?.TOTAL_RETAIL_DELIVERY_FEE || 0,
        retailDeliveryFeeJurisdiction: data?.trans?.retailDeliveryFeeJurisdiction
    };
};

export const reduceToCartItems = ({ response }: IRPCData<IOrderObject>): ICart['cartItems'] => {
    const { config } = diContainer.get(serviceNames.config);
    const displayGiftWrapItems = config?.cartConfig?.displayGiftWrapItems;
    const displayEngravedItems = config?.cartConfig?.displayEngravedItems;
    const { totals, order, appliedOffersData }: IOrderObject =
        response.data?.[0].result?.value || (defaultValue as unknown as IOrderObject);

    const orderTotals: IOrderTotals = totals || defaultValue.totals;
    const kits = order?.kits ? mapKits(order?.kits) : [];
    const filteredKits = displayEngravedItems ? kits : kits.filter((item) => !item.hasEngraving);

    const orderItems: IOrderItems[] = [
        ...(order.items.filter((item) => item?.isKitItem === 0 || item?.itemType === 'SkuItem') ||
            []),
        ...(order.samples.map((sample) => ({ ...sample, isSample: true })) || [])
    ];
    const cartItems = mapCartItems({
        orderItems,
        totals: orderTotals,
        cartAppliedOffersData: appliedOffersData
    });
    const filteredCartItems = displayGiftWrapItems
        ? cartItems
        : cartItems?.filter((item) => !item.isGiftItem);

    return [...filteredKits, ...filteredCartItems];
};

export const reduceToLastAddedItem = (
    response: IResponse<IPGResponse<IOrderObject>[]>,
    miniOverlayQtyAddedOnAtb: boolean
) => {
    const { ac_results, trans_data } = response.data?.[0].result.data || {};
    const lastAddedItem = (ac_results?.[0].result?.CARTITEM || {}) as IOrderItems;

    const itemWithQtyAdded = mapQtyAdded(ac_results?.[0]);
    const cartItem = miniOverlayQtyAddedOnAtb ? itemWithQtyAdded : lastAddedItem;
    const totals = trans_data?.totals || ({} as IOrderObject['totals']);

    const mappedLastAddedItem = mapCartItems({
        orderItems: [cartItem],
        totals,
        cartAppliedOffersData: undefined
    });

    return mappedLastAddedItem;
};

export const mapEventPayload = (response: IResponse<IPGResponse<IOrderObject>[]>) => {
    const { trans_data } = response.data?.[0].result.data || { response: {} };
    const { config } = diContainer.get(serviceNames.config);
    const miniOverlay = config?.cartConfig?.miniOverlay;
    const miniOverlayQtyAddedOnAtb = config?.cartConfig?.miniOverlayQtyAddedOnAtb;

    const transData = normalizeTransData(trans_data);

    const cartItems = miniOverlay
        ? reduceToLastAddedItem(response, miniOverlayQtyAddedOnAtb)
        : reduceToCartItems(transData);

    const cartTotals = parseTotals(transData.response);

    const cartOrderData = reduceToCartOrderData(transData);

    return { cartItems, cartTotals, cartOrderData, miniOverlay };
};
